import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  TableCell,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import api, { uploadFile } from '../config/api';
import DataTable from '../components/DataTable';

function Advertisements() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    banner_image_url: '',
    link_url: '',
    position: '',
    status: 'active',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const response = await api.get('/advertisements');
      setAds(response.data.data || []);
    } catch (error) {
      console.error('Error fetching advertisements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (ad = null) => {
    if (ad) {
      setEditing(ad);
      setFormData(ad);
      setImagePreview(ad.banner_image_url ? `${process.env.REACT_APP_API_URL + ad.banner_image_url}` : null);
      setSelectedFile(null);
    } else {
      setEditing(null);
      setFormData({
        title: '',
        banner_image_url: '',
        link_url: '',
        position: '',
        status: 'active',
      });
      setImagePreview(null);
      setSelectedFile(null);
    }
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setSelectedFile(null);
    setImagePreview(null);
    setError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setUploading(true);

      const submitFormData = new FormData();
      submitFormData.append('title', formData.title);
      submitFormData.append('link_url', formData.link_url || '');
      submitFormData.append('position', formData.position || '');
      submitFormData.append('status', formData.status);

      // If new file is selected, append it
      if (selectedFile) {
        submitFormData.append('banner_image', selectedFile);
      } else if (formData.banner_image_url && !editing) {
        // If URL is provided and it's a new ad, append URL
        submitFormData.append('banner_image_url', formData.banner_image_url);
      } else if (editing && formData.banner_image_url) {
        // If editing and URL is provided, append URL
        submitFormData.append('banner_image_url', formData.banner_image_url);
      }

      if (editing) {
        await uploadFile(`/advertisements/${editing.id}`, submitFormData, 'PUT');
      } else {
        await uploadFile('/advertisements', submitFormData, 'POST');
      }

      handleClose();
      fetchAds();
    } catch (error) {
      setError(error.response?.data?.error || 'Error saving advertisement');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this advertisement?')) {
      try {
        await api.delete(`/advertisements/${id}`);
        fetchAds();
      } catch (error) {
        console.error('Error deleting advertisement:', error);
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Advertisements</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} >
          Add Advertisement
        </Button>
      </Box>

      <DataTable
        data={ads}
        loading={loading}
        searchFields={['title']}
        emptyMessage="No advertisements found"
        columns={[
          { field: 'id', label: 'ID' },
          { field: 'title', label: 'Title' },
          { field: 'banner_image', label: 'Banner Image' },
          { field: 'status', label: 'Status' },
          { field: 'actions', label: 'Actions' },
        ]}
        renderRow={(ad) => (
          <TableRow key={ad.id}>
            <TableCell>{ad.id}</TableCell>
            <TableCell>{ad.title}</TableCell>
            <TableCell>
              {ad.banner_image_url ? (
                <img
                  src={`${process.env.REACT_APP_API_URL + ad.banner_image_url}`}
                  alt={ad.title}
                  style={{ width: 100, height: 50, objectFit: 'cover' }}
                />
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>{ad.status}</TableCell>
            <TableCell>
              <IconButton size="small" onClick={() => handleOpen(ad)}>
                <EditIcon />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(ad.id)}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        )}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Advertisement' : 'Add Advertisement'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            margin="normal"
            required
          />
          <Box sx={{ mb: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="banner-image-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="banner-image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ mb: 1 }}
              >
                {selectedFile ? selectedFile.name : 'Upload Banner Image'}
              </Button>
            </label>
            <Typography variant="caption" color="text.secondary">
              Max size: 1MB
            </Typography>
            {/* {!selectedFile && (
              <TextField
                fullWidth
                label="Or Enter Image URL"
                value={formData.banner_image_url}
                onChange={(e) => {
                  setFormData({ ...formData, banner_image_url: e.target.value });
                  setImagePreview(e.target.value);
                }}
                margin="normal"
                helperText="Upload file or provide image URL"
              />
            )} */}
            {imagePreview && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 4 }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {selectedFile && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedFile(null);
                      setImagePreview(editing ? formData.banner_image_url : null);
                    }}
                    sx={{ mt: 1 }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            )}
          </Box>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Redirect Options
          </Typography>
          <TextField
            fullWidth
            label="Website URL"
            value={formData.link_url || ''}
            onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
            margin="normal"
            placeholder="https://example.com"
            helperText="Optional: URL to redirect when advertisement is clicked"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={uploading}>
            {uploading ? <CircularProgress size={20} /> : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Advertisements;

